/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from 'store';
import { createProposal } from 'store/modules/conlicitaseg_store/actions';
import { ConlicitasegState } from 'store/modules/conlicitaseg_store/types';
import createTrack from 'services/actions/tracker_actions';
import {
  formatCurrency,
  formatCNPJ,
  formatDate,
  formatInsuranceName,
  getInsuranceReason,
  calculateCoverage,
} from 'services/conlicitaseg';
import SquareLoader from 'components/atoms/square_loader';
import PolicySuccess from 'components/atoms/policy_success';
import PolicyAnalysis from 'components/atoms/policy_analysis';
import PolicyError from 'components/atoms/policy_error';

const InsuranceSuccess: React.FC = () => {
  const history = useHistory();
  const shouldRedirect = useRef(false);
  const [showPolicyResult, setShowPolicyResult] = useState<string>('');

  const { selected_quote, request, processes } = useSelector<ApplicationState, ConlicitasegState>(
    (state) => state.session.conlicitaseg,
  );

  const insurance = processes.find(
    (process) => process.id === Number(selected_quote.insurance_process_id),
  );

  const getInstallmentText = () => {
    if (selected_quote.installments === '1') {
      return 'À vista';
    }
    return `${selected_quote.installments}x`;
  };

  const getDueDateText = () => {
    if (selected_quote.due_date) {
      return ` | Vencimento: ${formatDate(selected_quote.due_date)}`;
    }
    return '';
  };

  const handleBack = () => {
    history.goBack();
  };

  const dispatch = useDispatch();

  const handleCreateProposal = () => {
    dispatch(
      createProposal(
        selected_quote?.selected_api_quote_id || '',
        selected_quote?.insurance_company_id || '',
        selected_quote?.proposal_id || '',
        selected_quote?.insurance_process_id || 0,
      ),
    );
    shouldRedirect.current = true;
    createTrack('ConlicitaSeg - Clicou para Emitir Apólice');
  };

  const contractGuaranteeValue = formatCurrency(
    calculateCoverage(insurance?.contract_value || 0, insurance?.contract_percentage || 0),
    { emptyOnZero: true },
  );

  useEffect(() => {
    if (selected_quote.status_id === 'emitida') {
      setShowPolicyResult('sucesso');
    } else if (selected_quote.status_id === 'analise') {
      setShowPolicyResult('analise');
    }
  }, [selected_quote.status_id]);

  useEffect(() => {
    if (request?.create_proposal?.error && shouldRedirect.current) {
      setShowPolicyResult('erro');
      shouldRedirect.current = false;
    }
  }, [request?.create_proposal?.error]);

  return (
    <div className="page-container">
      {showPolicyResult === 'sucesso' && <PolicySuccess />}
      {showPolicyResult === 'analise' && <PolicyAnalysis />}
      {showPolicyResult === 'erro' && <PolicyError />}

      {showPolicyResult === '' && (
        <div className="container-fluid big-resolutions py-5 px-md-5">
          <div className="row justify-content-center mt-5">
            <div className="col-12 mx-auto">
              <button
                type="button"
                className="mb-3 px-0 btn btn-link text-primary"
                onClick={handleBack}
              >
                <i className="material-icons align-middle">keyboard_arrow_left</i> Voltar para etapa
                anterior
              </button>

              <div className="shadow-sm p-4 bg-white rounded">
                <h3 className="mb-2 text-dark">
                  Você está a 1 clique de emitir o Seguro Garantia desta licitação!
                </h3>
                <div className="mb-3">
                  Para finalizarmos a emissão da apólice, revise atentamente os dados a seguir.
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="border p-3 rounded h-100">
                      <div className="mb-3">
                        <strong>Motivo do seguro</strong>
                        <div className="text-secondary">
                          {getInsuranceReason(insurance?.type_warranty_id || '')}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Empresa que está contratando o seguro (Tomador)</strong>
                        <div className="text-secondary">
                          {formatCNPJ(insurance?.policyholder_cnpj)} -{' '}
                          {insurance?.policyholder_company_name}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Órgão público que solicitou o seguro (Segurado)</strong>
                        <div className="text-secondary">
                          {formatCNPJ(insurance?.insured.cnpj)} - {insurance?.insured.name}
                        </div>
                        <div className="text-secondary">
                          {`${insurance?.insured.address}, ${insurance?.insured.number}, ${insurance?.insured.district}, ${insurance?.insured.city}/${insurance?.insured.state}, ${insurance?.insured.zip_code}`}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Modalidade da licitação</strong>
                        <div className="text-secondary">{insurance?.bidding_modality}</div>
                      </div>

                      <div className="mb-3">
                        <strong>N° do edital</strong>
                        <div className="text-secondary">{insurance?.bidding_edict}</div>
                      </div>

                      {insurance?.bidding_contract_number && (
                        <div className="mb-3">
                          <strong>N° do contrato</strong>
                          <div className="text-secondary">{insurance?.bidding_contract_number}</div>
                        </div>
                      )}

                      {insurance?.bidding_process_number && (
                        <div className="mb-3">
                          <strong>N° do processo</strong>
                          <div className="text-secondary">{insurance?.bidding_process_number}</div>
                        </div>
                      )}

                      <div className="mb-3">
                        <strong>Vigência do seguro</strong>
                        <div className="text-secondary">
                          {formatDate(insurance?.contract_start_date)} até{' '}
                          {formatDate(insurance?.contract_end_date)} (
                          {insurance?.contract_days || 0} dias)
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mt-3 mt-md-0">
                    <div className="border p-3 rounded h-100">
                      <div className="mb-3">
                        <strong>
                          {insurance?.type_warranty_id === 'Licitante'
                            ? 'Valor estimado'
                            : 'Valor do contrato'}
                        </strong>
                        <div className="text-secondary">
                          {formatCurrency(insurance?.contract_value || 0, { emptyOnZero: true })}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Percentual da garantia</strong>
                        <div className="text-secondary">{insurance?.contract_percentage || 0}%</div>
                      </div>

                      {insurance?.contract_additional_value &&
                        insurance?.contract_additional_value > 0 && (
                          <>
                            <div className="mb-3">
                              <strong>Valor da garantia contratual</strong>
                              <div className="text-secondary">{contractGuaranteeValue}</div>
                            </div>
                            <div className="mb-3">
                              <strong>Valor da garantia adicional</strong>
                              <div className="text-secondary">
                                {formatCurrency(insurance?.contract_additional_value, {
                                  emptyOnZero: true,
                                })}
                              </div>
                            </div>
                          </>
                        )}

                      <div className="mb-3">
                        <strong>Valor total da cobertura</strong>
                        <div className="text-secondary">
                          {formatCurrency(insurance?.contract_insured_amount || 0, {
                            emptyOnZero: true,
                          })}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Seguradora escolhida</strong>
                        <div className="text-secondary">
                          {formatInsuranceName(
                            (selected_quote.insurance_company_id || '').toLowerCase(),
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Valor do seguro</strong>
                        <div className="text-secondary">
                          {formatCurrency(selected_quote.premium_value || '0', {
                            emptyOnZero: true,
                          })}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Condição de pagamento</strong>
                        <div className="text-secondary">
                          {getInstallmentText()}
                          {getDueDateText()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateProposal}
                    disabled={request?.create_proposal?.loading}
                  >
                    {request?.create_proposal?.loading ? (
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        Gerando apólice
                        <SquareLoader customClass="conlicitaseg-continue-quote" />
                      </span>
                    ) : (
                      'Gerar apólice do Seguro Garantia'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceSuccess;
